import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXS, LabelL } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { ContenderRanking } from '#src/components/lineup/Contender/ContenderFooter/ContenderRanking/index.tsx';

import type { RankingInfo_so5Ranking } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 2.5, center: true })`
  width: 100%;
  padding: 0 var(--double-unit);
`;
const SmallGapHorizontal = styled(Horizontal).attrs({ gap: 0.5 })``;
const ScoreInfoWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content 1fr;
  column-gap: var(--double-unit);
  align-items: center;

  & > :first-child {
    margin-left: auto;
  }
  & > :last-child {
    margin-right: auto;
  }
`;

type Props = { so5Ranking?: RankingInfo_so5Ranking | null };
export const RankingInfo = ({ so5Ranking }: Props) => {
  if (!so5Ranking) {
    return null;
  }

  return (
    <Root>
      <LabelL bold>{so5Ranking?.so5Lineup.name}</LabelL>
      {!!so5Ranking && (
        <ScoreInfoWrapper>
          <ContenderRanking Typography={BrandM} ranking={so5Ranking.ranking} />
          <svg height={15} width={1} xmlns="http://www.w3.org/2000/svg">
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="200"
              stroke="var(--c-nd-600)"
              strokeWidth={2}
            />
          </svg>
          <BrandM as={SmallGapHorizontal}>
            <FormattedMessage
              {...fantasy.ordinalPoints}
              values={{
                score: (
                  <FormattedNumber
                    value={so5Ranking.score}
                    maximumFractionDigits={0}
                  />
                ),
                sup: content => (
                  <BrandXS color="var(--c-nd-600)" as="sup">
                    {content}
                  </BrandXS>
                ),
              }}
            />
          </BrandM>
        </ScoreInfoWrapper>
      )}
    </Root>
  );
};

RankingInfo.fragments = {
  so5Ranking: gql`
    fragment RankingInfo_so5Ranking on So5Ranking {
      id
      score
      ranking
      so5Lineup {
        id
        name
      }
    }
  ` as TypedDocumentNode<RankingInfo_so5Ranking>,
};
